import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth, homeFor } from "../context/AuthContext";
import { useCountry } from "../context/CountryContext";
import { errMsg } from "../lib/api";

const Shell = ({ title, sub, children, testid }) => (
  <div className="grid min-h-[80vh] place-items-center bg-background px-4 py-12" data-testid={testid}>
    <div className="w-full max-w-md rounded-lg border bg-white p-8">
      <h1 className="text-3xl font-bold">{title}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{sub}</p>
      <div className="mt-6">{children}</div>
    </div>
  </div>
);

const Field = ({ label, ...p }) => (
  <div><Label>{label}</Label><Input className="mt-1.5" {...p} /></div>
);

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(f.email, f.password);
      nav(sp.get("next") || homeFor(u));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Sign in" sub="Customers, suppliers and admins all sign in here." testid="login-page">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email-input" />
        <Field label="Password" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" />
        <Button className="h-11 w-full" disabled={busy} data-testid="login-submit-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}</Button>
      </form>
      <p className="mt-6 text-sm text-muted-foreground">New here? <Link to={`/register${sp.get("next") ? `?next=${sp.get("next")}` : ""}`} className="font-semibold text-primary" data-testid="go-register-link">Create an account</Link></p>
    </Shell>
  );
}

export function Register() {
  const { register } = useAuth();
  const { code } = useCountry();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register({ ...f, country: code });
      toast.success("Welcome to HostAfricair");
      nav(sp.get("next") || "/portal");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Create account" sub="One account for internet, domains and billing." testid="register-page">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Full name" required value={f.name} onChange={set("name")} data-testid="register-name-input" />
        <Field label="Email" type="email" required value={f.email} onChange={set("email")} data-testid="register-email-input" />
        <Field label="Phone" value={f.phone} onChange={set("phone")} data-testid="register-phone-input" />
        <Field label="Password" type="password" required minLength={6} value={f.password} onChange={set("password")} data-testid="register-password-input" />
        <Button className="h-11 w-full" disabled={busy} data-testid="register-submit-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}</Button>
      </form>
      <p className="mt-6 text-sm text-muted-foreground">Already have an account? <Link to="/login" className="font-semibold text-primary">Sign in</Link></p>
    </Shell>
  );
}
